/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import ExploreGoalsAndDomains from 'bundles/composable-page-header/components/mobile-menu-items/ExploreGoalsAndDomains';
import MobileBackNavHeader from 'bundles/composable-page-header/components/mobile-menu-items/MobileBackNavHeader';
import type { MobileExploreIdentifierValues } from 'bundles/composable-page-header/constants';
import { MOBILE_EXPLORE_IDENTIFIER, MOBILE_MAIN_IDENTIFIER } from 'bundles/composable-page-header/constants';
import { useMobileToParentPage } from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import SimplifiedMegaMenuSection from 'bundles/megamenu/components/sections/SimplifiedMegaMenuSection';
import CertificatesTab from 'bundles/megamenu/components/tabs/CertificatesTab';
import DegreesTab from 'bundles/megamenu/components/tabs/DegreesTab';
import DomainTab from 'bundles/megamenu/components/tabs/DomainTab';
import type { MenuItem } from 'bundles/megamenu/types/MenuData';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  exploreViewState: MobileExploreIdentifierValues;
  setExploreViewState: (state: MobileExploreIdentifierValues) => void;
  setMenuItem: (item: MenuItem | undefined) => void;
  currentMenuItem: MenuItem | undefined;
  backToExplore: React.ReactNode;
};

const exploreIdToFilterMapping = {
  [MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_ROLES_SUBMENU]: 'career',
  [MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_CATEGORIES_SUBMENU]: 'categories',
  [MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_SKILLS_SUBMENU]: 'skills',
  [MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_SKILLS_SUBMENU]: 'skills',
  [MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_PROGRAM_SUBMENU]: 'from-program',
  [MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_CATEGORIES_SUBMENU]: 'categories',
  [MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_MORE_SUBMENU]: 'explore-more-from-coursera',

  [MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_PROFESSIONAL_CERTIFICATE_SUBMENU]: 'professional-certificates',

  [MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_ONLINE_DEGREE_SUBMENU]: 'online-degrees',
} as const;

const ExploreSubPage = ({
  exploreViewState,
  setExploreViewState,
  setMenuItem,
  currentMenuItem,
  backToExplore,
}: Props) => {
  const toParentPage = useMobileToParentPage();

  switch (exploreViewState) {
    case MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_SUBMENU:
      return (
        <React.Fragment>
          <MobileBackNavHeader
            label={_t('Main Menu')}
            trackingName="mobile_header_back_to_main_menu_button"
            trackingData={{ name: `Go to main navigation slide` }}
            ariaLabel={_t('Go to main navigation slide')}
            onClick={() => toParentPage?.(MOBILE_MAIN_IDENTIFIER.MAIN_MENU)}
            key="header-back"
          />
          <ExploreGoalsAndDomains
            key="goals-and-domains"
            setExploreViewState={setExploreViewState}
            setMenuItem={setMenuItem}
          />
        </React.Fragment>
      );
    case MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_ROLES_SUBMENU:
    case MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_CATEGORIES_SUBMENU:
    case MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_SKILLS_SUBMENU:
    case MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_PROFESSIONAL_CERTIFICATE_SUBMENU:
    case MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_ONLINE_DEGREE_SUBMENU:
      return (
        <React.Fragment>
          <MobileBackNavHeader
            label={_t('Main Menu')}
            trackingName="mobile_header_back_to_main_menu_button"
            trackingData={{ name: `Go to main navigation slide` }}
            ariaLabel={_t('Go to main navigation slide')}
            onClick={() => toParentPage?.(MOBILE_MAIN_IDENTIFIER.MAIN_MENU)}
            key="header-back"
          />
          <SimplifiedMegaMenuSection
            isMobile={true}
            noMobileHeaderPadding={true}
            filterSection={exploreIdToFilterMapping[exploreViewState]}
          />
        </React.Fragment>
      );
    case MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_SKILLS_SUBMENU:
    case MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_PROGRAM_SUBMENU:
    case MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_CATEGORIES_SUBMENU:
    case MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_MORE_SUBMENU:
      return (
        <SimplifiedMegaMenuSection
          isMobile={true}
          noMobileHeaderPadding={true}
          filterSection={exploreIdToFilterMapping[exploreViewState]}
        />
      );
    case MOBILE_EXPLORE_IDENTIFIER.DEGREES_MENU:
      if (!currentMenuItem) return null;
      return (
        <React.Fragment>
          {backToExplore}
          <DegreesTab
            key={currentMenuItem.id}
            data={currentMenuItem?.subMenuData}
            isMobile={true}
            closeMenu={() => {}}
          />
          ,
        </React.Fragment>
      );
    case MOBILE_EXPLORE_IDENTIFIER.CERTIFICATE_MENU:
      if (!currentMenuItem) return null;
      return (
        <React.Fragment>
          {backToExplore}
          <CertificatesTab
            key={currentMenuItem.id}
            data={currentMenuItem?.subMenuData}
            isMobile={true}
            closeMenu={() => {}}
          />
          ,
        </React.Fragment>
      );
    case MOBILE_EXPLORE_IDENTIFIER.DOMAIN_MENU:
      if (!currentMenuItem) return null;
      return (
        <React.Fragment>
          {backToExplore}
          <DomainTab key={currentMenuItem.id} menuItem={currentMenuItem} isMobile={true} closeMenu={() => {}} />,
        </React.Fragment>
      );
    default:
      return null;
  }
};

export default ExploreSubPage;
